import React from 'react';
import clsx from 'clsx';

const styles = {
  base: [
    'block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6',
    'dark:bg-zinc-900 dark:text-white dark:placeholder:text-gray-500',
  ],
  variants: {
    default: [
      'ring-gray-300 focus:ring-blue-600',
      'dark:ring-gray-700 dark:focus:ring-blue-500',
    ],
    error: [
      'ring-red-500 focus:ring-red-600 text-red-900 placeholder:text-red-400',
      'dark:text-red-400 dark:placeholder:text-red-400',
    ],
    success: [
      'ring-green-500 focus:ring-green-600 text-green-900 placeholder:text-green-400',
      'dark:text-green-400 dark:placeholder:text-green-400',
    ],
  },
  sizes: {
    sm: 'px-2 py-1 text-sm',
    md: 'px-3 py-2',
    lg: 'px-4 py-3 text-lg',
  },
  disabled: 'opacity-50 cursor-not-allowed bg-gray-100 dark:bg-zinc-800',
  icon: 'pl-10',
};

export const Input = React.forwardRef(({
  label,
  value, 
  onChange, 
  placeholder, 
  type = 'text', 
  className = '',
  error = '',
  helperText = '',
  required = false,
  disabled = false,
  name = '',
  variant = 'default',
  size = 'md',
  icon = null,
  fullWidth = true,
}, ref) => {
  // Determine variant based on props
  let variantToUse = variant;
  if (error) variantToUse = 'error';

  const errorTextColor = 'text-red-600 dark:text-red-400';

  return (
    <div className={clsx('relative', fullWidth ? 'w-full' : 'w-auto')}>
      {label && (
        <label 
          className={clsx(
            'block text-sm font-medium mb-1 text-gray-900 dark:text-gray-200',
            error && errorTextColor
          )}
        >
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      
      <div className="relative">
        {icon && (
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-gray-500">
            {icon}
          </div>
        )}
        
        <input
          ref={ref}
          type={type}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          disabled={disabled}
          name={name}
          required={required}
          className={clsx(
            styles.base,
            styles.variants[variantToUse],
            styles.sizes[size],
            icon && styles.icon,
            disabled && styles.disabled,
            className
          )}
        />
      </div>
      
      {(error || helperText) && (
        <p 
          className={clsx(
            'mt-1 text-sm',
            error && errorTextColor,
            !error && 'text-gray-500 dark:text-gray-400'
          )}
          id={`${name}-description`}
        >
          {error || helperText}
        </p>
      )}
    </div>
  );
});

Input.displayName = 'Input';

export const TextField = Input;
